import { React, useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { FiBatteryCharging, FiWifi } from "react-icons/fi";
import { useVisitorCount } from "../components/common/useVisitorCount";
import Loader from "../components/common/Loader";

const Contact = () => {
  const [animationStarted, setAnimationStarted] = useState(false);
  const visitCount = useVisitorCount();
  const shouldReduceMotion = useReducedMotion();

  const { ref, inView } = useInView({
    threshold: 0.25,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) setAnimationStarted(true);
  }, [inView]);

  // Only show loader when visitCount is null/undefined (allow 0)
  if (visitCount == null) return <Loader />;

  return (
    <section className="bg-pink-50 overflow-x-hidden">
      <div className="mx-auto max-w-screen-lg px-4 sm:px-6 py-10 sm:py-12">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-pink-600 mb-3 sm:mb-4">
            Contact Us
          </h1>
          <p className="text-pink-500 text-sm sm:text-base">
            Chat with our friendly team — we’re here to help!
          </p>
        </div>

        {/* Main Content */}
        <div id="contact" className="flex flex-col-reverse lg:flex-row gap-8 lg:gap-10 items-center">
          {/* FloatingPhone Animation */}
          <motion.div
            ref={ref}
            className="flex justify-center w-full lg:w-2/5"
            initial={shouldReduceMotion ? {} : { x: "-40%", opacity: 0 }}
            animate={animationStarted && !shouldReduceMotion ? { x: 0, opacity: 1 } : {}}
            transition={{ duration: 1.1, ease: "easeOut" }}
          >
            <FloatingPhone reduce={shouldReduceMotion} />
          </motion.div>

          {/* Map */}
          <div className="w-full lg:w-3/5">
            <div className="rounded-lg overflow-hidden shadow-md aspect-[16/10] sm:aspect-[16/9]">
              <iframe
                title="BeConfident Office"
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d7771.643974822825!2d80.213509!3d13.110461!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a52655b4cabeecd%3A0xf9bb1af6542575ea!2sBeConfident!5e0!3m2!1sen!2sin!4v1719778032659!5m2!1sen!2sin"
                className="w-full h-full"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mt-10 sm:mt-12">
          <ContactCard
            icon={<EmailIcon />}
            title="Email Us"
            detail="contact@beconfident.com"
          />
          <ContactCard
            icon={<LocationIcon />}
            title="Visit Us"
            detail="123 Pink Street, Chennai, TN 600082"
          />
          <ContactCard
            icon={<FiBatteryCharging className="text-pink-600 text-xl" />}
            title="Call Us"
            detail="+91 9597292925"
          />
        </div>

        {/* Visitor Count */}
        <div className="mt-10 sm:mt-12 text-center">
          <h2 className="text-xl sm:text-2xl font-semibold text-pink-600">Visitor Count</h2>
          <p className="text-pink-500 text-lg sm:text-xl mt-2">{visitCount}</p>
        </div>
      </div>
    </section>
  );
};

const ContactCard = ({ icon, title, detail }) => (
  <div className="bg-white p-5 sm:p-6 rounded-lg shadow transition hover:shadow-lg text-center">
    <div className="bg-pink-100 inline-flex p-3 rounded-full mb-3 sm:mb-4">{icon}</div>
    <h3 className="text-base sm:text-lg font-medium text-pink-600 mb-1">{title}</h3>
    <p className="text-pink-500 text-sm leading-snug break-words">
      {detail}
    </p>
  </div>
);

// SVG Icons
const EmailIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-5 h-5 sm:w-6 sm:h-6 text-pink-600"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
    />
  </svg>
);

const LocationIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-5 h-5 sm:w-6 sm:h-6 text-pink-600"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
    />
  </svg>
);

const FloatingPhone = ({ reduce }) => (
  <div className="relative w-36 h-64 sm:w-48 sm:h-80">
    <div
      style={{ transformStyle: "preserve-3d", transform: "rotateY(-16deg) rotateX(8deg)" }}
      className="absolute inset-0 bg-pink-200 rounded-2xl shadow-inner"
      aria-hidden="true"
    />
    <motion.div
      initial={reduce ? {} : { y: 8 }}
      animate={reduce ? {} : { y: -8 }}
      transition={reduce ? {} : { repeat: Infinity, repeatType: "reverse", duration: 1.8, ease: "easeInOut" }}
      className="relative h-full w-full bg-white border-2 border-pink-300 rounded-2xl p-4 flex flex-col justify-between"
      role="img"
      aria-label="Decorative phone mockup"
    >
      <HeaderBar />
      <Screen />
    </motion.div>
  </div>
);

const HeaderBar = () => (
  <div className="flex justify-between items-center mb-2 text-pink-400 text-sm">
    <FiWifi aria-label="wifi" />
    <FiBatteryCharging aria-label="battery" />
  </div>
);

const Screen = () => (
  <div className="flex-grow flex items-center justify-center">
    <span className="text-pink-600 font-bold text-lg sm:text-xl">BeConfident</span>
  </div>
);

export default Contact;
